import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdTaskTopicDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { useTaskTablesContext } from '../../context';

export const TopicFilter: FC = () => {
  const [t] = useTranslation(['backend', 'common']);
  const filters = useTaskTablesContext('filters$');
  const { updateFilter, topicPrefix, setTopicPrefix, topicRequest } = useTaskTablesContext();

  const options = useSubscribe(
    topicRequest.data$,
    (response) => response?.data.items.flatMap((item) => (item.id ? { label: item.name, value: item.id } : [])) ?? [],
  );
  const total = useSubscribe(topicRequest.data$, (response) => response?.data.total ?? 0);
  const loading = useSubscribe(topicRequest.pending$);

  const handleChange = useCallbackRef((option: Option<IdTaskTopicDto> | null) => {
    updateFilter('topic', option);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: topicPrefix, setFilter: setTopicPrefix, total }),
    [setTopicPrefix, topicPrefix, total],
  );

  return (
    <Filter<IdTaskTopicDto>
      label={t('common:TOPIC')}
      options={options}
      selected={filters.topic}
      onChange={handleChange}
      loading={loading}
      serverSideFiltering={serverSideFiltering}
      isClearable
      dataTestId='topic-filter'
    />
  );
};
